import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSeasonContext } from '../context/SeasonContext';
import { useIsMobile } from '../hooks/shared/useIsMobile';

const nextYear = () => new Date().getFullYear() + 1;

const SeasonSelector = () => {
  const { currentSeason, availableSeasons, loading, changeSeason, createSeason } = useSeasonContext();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newSeasonYear, setNewSeasonYear] = useState(nextYear);
  const [createError, setCreateError] = useState('');
  const [saving, setSaving] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsCreating(false);
    setCreateError('');
    setNewSeasonYear(nextYear());
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const year = Number(newSeasonYear);
    if (!Number.isInteger(year) || year < 2000 || year > 2100) {
      setCreateError('Podaj rok z zakresu 2000–2100.');
      return;
    }
    if (availableSeasons.includes(year)) {
      setCreateError(`Sezon ${year} już istnieje.`);
      return;
    }

    setSaving(true);
    setCreateError('');
    try {
      await createSeason(year);
      close();
    } catch (err) {
      setCreateError('Nie udało się utworzyć sezonu. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const pickSeason = (season) => {
    changeSeason(season);
    close();
  };

  if (loading) {
    return (
      <span className="season-selector-trigger" aria-live="polite">
        <i className="fas fa-calendar-alt" />
        <span>Ładowanie…</span>
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
      </span>
    );
  }

  const panel = (
    <>
      <button type="button" className="season-selector-backdrop" onClick={close} aria-label="Zamknij wybór sezonu" />
      <div className="season-selector-panel" role="dialog" aria-label="Wybór sezonu">
        <div className="season-selector-handle" />
        <div className="season-selector-title">Dostępne sezony</div>

        <div className="season-selector-options">
          {availableSeasons.map((season) => (
            <button
              key={ season }
              type="button"
              className={ `season-selector-option ${season === currentSeason ? 'active' : ''}` }
              aria-current={ season === currentSeason ? 'true' : undefined }
              onClick={ () => pickSeason(season) }
            >
              <i className="fas fa-calendar" />
              <span>Sezon {season}</span>
              {season === currentSeason && <i className="fas fa-check season-selector-check" />}
            </button>
          ))}
        </div>

        {isCreating ? (
          <form className="season-selector-create" onSubmit={ handleCreate }>
            <label htmlFor="season-selector-year">Rok nowego sezonu</label>
            <input
              id="season-selector-year"
              className="form-control"
              type="number"
              inputMode="numeric"
              min="2000"
              max="2100"
              value={ newSeasonYear }
              onChange={ (e) => setNewSeasonYear(e.target.value) }
              autoFocus
            />
            {createError && <p className="season-selector-error">{createError}</p>}
            <div className="season-selector-create-actions">
              <button type="button" className="btn btn-outline-secondary" onClick={ () => setIsCreating(false) }>
                Anuluj
              </button>
              <button type="submit" className="btn btn-success" disabled={ saving }>
                {saving ? 'Tworzenie…' : 'Utwórz'}
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="season-selector-add" onClick={ () => setIsCreating(true) }>
            <i className="fas fa-plus" />
            <span>Utwórz nowy sezon</span>
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="season-selector">
      <button
        type="button"
        className="season-selector-trigger"
        onClick={ () => (isOpen ? close() : setIsOpen(true)) }
        aria-expanded={ isOpen }
        aria-haspopup="dialog"
      >
        <i className="fas fa-calendar-alt" />
        <span>Sezon {currentSeason}</span>
        <i className={ `fas fa-chevron-${isOpen ? 'up' : 'down'} season-selector-caret` } />
      </button>

      {/* On a phone the panel is a bottom sheet. The sticky top bar it opens from
          is a stacking context, so left in place the sheet would sit underneath
          the bottom nav; the portal is what gets it out. */}
      {isOpen && (isMobile ? createPortal(panel, document.body) : panel)}
    </div>
  );
};

export default SeasonSelector;
